import { Power2, gsap } from 'gsap'

/** Slack on top of the tween duration before a reveal is considered stalled. */
const REVEAL_GRACE_MS = 400

/**
 * Guarantees an enter tween ends up revealing its node.
 *
 * GSAP advances on requestAnimationFrame, which browsers do not fire for an iframe they are not
 * painting (the Propstack embed mounts hidden). A reveal tween then sticks on its `from` state —
 * `autoAlpha: 0` — and the content stays invisible for good. Timers keep running there, so snap the
 * timeline to its end state if it has not finished in time. One call covers a staggered timeline:
 * `totalProgress(1)` completes the parent tween and every child tween on it.
 */
export const ensureRevealed = (timeline: gsap.core.Timeline): void => {
  window.setTimeout(
    () => { if (timeline.totalProgress() < 1) timeline.totalProgress(1) },
    timeline.totalDuration() * 1000 + REVEAL_GRACE_MS
  )
}

/** The standard widely used from {autoAlpha: 0, height: 0} to {autoAlpha: 1, height: 'auto'} in 0.3s Power2.easeIn effect */
export const standardEnterEffect = (node: HTMLElement) => {
  if (!node) return
  if (!node.classList) return
  const timelineEnter = gsap.timeline({ paused: true })
  timelineEnter.totalProgress(1).clear(true).totalProgress(0)
  node.classList.add('entered')
  timelineEnter.fromTo(node, { autoAlpha: 0, height: 0, ease: Power2.easeIn }, { autoAlpha: 1, height: 'auto', ease: Power2.easeIn, duration: 0.3 })
  timelineEnter.play()
  ensureRevealed(timelineEnter)
}

/** The from {autoAlpha: 0, height: 0} to {autoAlpha: 1, height: 'auto'} in 0.3s Power2.easeIn with tween 1s effect */
export const standardEnterEffectWithTween = (node: HTMLElement) => {
  if (!node) return
  if (!node.classList) return
  const timelineEnter = gsap.timeline({ paused: true })
  timelineEnter.totalProgress(1).clear(true).totalProgress(0)
  node.classList.add('entered')
  timelineEnter.fromTo(node, { autoAlpha: 0, height: 0, ease: Power2.easeIn }, { autoAlpha: 1, height: 'auto', ease: Power2.easeIn, duration: 0.3 }, '+=1')
  timelineEnter.play()
  ensureRevealed(timelineEnter)
}

/** The standard widely used to {autoAlpha: 0, height: 0} in 0.3s Power2.easeIn effect */
export const standardExitEffect = (node: HTMLElement) => {
  if (!node) return
  if (!node.classList) return
  const timelineExit = gsap.timeline({ paused: true })
  timelineExit.totalProgress(1).clear(true).totalProgress(0)
  node.classList.remove('entered')
  timelineExit.to(node, { autoAlpha: 0, height: 0, ease: Power2.easeIn, duration: 0.3 })
  timelineExit.play()
}

/** Enter fade-in effect used to slowly fade elements in (autoAlpha: 0 -> 1 in 0.3 sec with Power2.easeIn) */
export const fadeInEnterEffect = (node: HTMLElement) => {
  if (!node) return
  if (node.nodeType === node.TEXT_NODE) return
  const timelineEnter = gsap.timeline({ paused: true })
  timelineEnter.totalProgress(1).clear(true).totalProgress(0)
  timelineEnter.fromTo(node, { autoAlpha: 0, ease: Power2.easeIn }, { autoAlpha: 1, ease: Power2.easeIn, duration: 0.3 }, 0)
  timelineEnter.play()
  ensureRevealed(timelineEnter)
}

/** Exit fade-out effect used to slowly fade elements out (autoAlpha: 1 -> 0 in 0.3 sec with Power2.easeIn) */
export const fadeOutExitEffect = (node: HTMLElement) => {
  if (!node) return
  if (node.nodeType === node.TEXT_NODE) return
  const timelineExit = gsap.timeline({ paused: true })
  timelineExit.totalProgress(1).clear(true).totalProgress(0)
  timelineExit.fromTo(node, { autoAlpha: 1, ease: Power2.easeIn }, { autoAlpha: 0, ease: Power2.easeIn, duration: 0.3 }, 0)
  timelineExit.play()
}

/** The standard widely used {enter: 300, exit: 300} timeout */
export const standardTimeoutConfig = { enter: 300, exit: 300 }
